import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Session } from "../data";
import { t } from "../lib/sprache";
import { forgetPendingConsentUnlessFor } from "../lib/zustimmung-vormerkung";

/** Wer ist angemeldet, in welchen Agenturen, mit welcher Rolle und welchen Rechten. */

export type OrgRole = "chef_manager" | "manager";
export type Perm = "approve_invoices" | "send_invoices" | "dun" | "confirm_payments" | "trigger_payouts" | "sign_contracts" | "view_financials" | "view_amounts"
  | "manage_team" | "manage_settings" | "manage_billing" | "edit_templates" | "edit_contract_text";

export interface Membership {
  id: string;
  org_id: string;
  role: OrgRole;
  display_name: string | null;
  job_title: string | null;
  sees_all_talents: boolean;
  can_approve_invoices: boolean;
  can_send_invoices: boolean;
  can_dun: boolean;
  can_confirm_payments: boolean;
  can_trigger_payouts: boolean;
  can_sign_contracts: boolean;
  /** Vorlagen bearbeiten (0112) */
  can_edit_templates?: boolean;
  /** Vertragstext im Einzelvertrag ändern (0112) */
  can_edit_contract_text?: boolean;
  /** Kennzahlen und Agenturumsatz sehen (0037); fehlt in älteren Zeilen = false */
  can_view_financials?: boolean;
  /** Beträge sehen: Honorare, Provisionen, Umsätze (0057); fehlt in älteren Zeilen = true */
  can_view_amounts?: boolean;
  /** Inhaber der Agentur (0057) */
  is_owner?: boolean;
}
export interface TalentLink {
  talent_id: string;
  org_id: string;
}
export interface OrgProfile {
  org_id: string;
  name: string;
  legal_name: string | null;
  logo_path: string | null;
  brand_color: string | null;
  locale: string;
  currency: string;
  timezone: string;
  country: string | null;
  default_document_language: string | null;
  /** Eigener Portalname (org_portal_names, 0057); leer: Wortmarke der Plattform */
  portal_name?: string | null;
  /** Art des Kontos (0178): Agentur oder selbständiger Creator; fehlt (Creator-Sicht, ältere Datenbank) = Agentur */
  kind?: "agency" | "creator";
}
export interface Account {
  session: Session;
  memberships: Membership[];
  talentLinks: TalentLink[];
  orgs: Record<string, OrgProfile>;
  isPlatformAdmin: boolean;
  /** Betreiberrechte nur mit zweitem Faktor (platform_admins.mfa_required, 0085); fehlt der Wert, gilt die Pflicht */
  platformMfaRequired?: boolean;
  initials: string | null;
  /** Eigene Oberflächensprache (user_preferences.language, PARITAET-8); leer: Sprache der Agentur */
  language?: string | null;
  /** Eigenes Datumsformat (user_preferences.date_format, PARITAET-R3-7); leer: nach der Sprache */
  dateFormat?: string | null;
  /** 0330: Startseite nach dem Login für Plattform-Admins (user_preferences.start_area); leer: Betrieb */
  startArea?: "betrieb" | "agentur" | null;
  talentNames: Record<string, string>;
}

type Prefs = { initials: string | null; language: string | null; date_format?: string | null; start_area?: string | null };

async function loadAccount(session: Session): Promise<Account> {
  const [memberships, talentLinks, admins, prefs] = await Promise.all([
    api.select<Membership>("memberships", { filters: [["user_id", "eq", session.userId], ["active", "is", true]] }),
    api.select<TalentLink>("talent_user_links", { columns: "talent_id, org_id", filters: [["user_id", "eq", session.userId]] }),
    api.select<{ user_id: string; mfa_required?: boolean }>("platform_admins", { filters: [["user_id", "eq", session.userId]] }),
    api.select<Prefs>("user_preferences", { columns: "initials, language, date_format, start_area", filters: [["user_id", "eq", session.userId]] })
      // ältere Datenbank ohne Spalte start_area (vor 0330) bzw. date_format (vor 0098): ohne diese Angaben
      .catch(() => api.select<Prefs>("user_preferences", { columns: "initials, language, date_format", filters: [["user_id", "eq", session.userId]] }))
      .catch(() => api.select<Prefs>("user_preferences", { columns: "initials, language", filters: [["user_id", "eq", session.userId]] })),
  ]);
  const orgIds = [...new Set([...memberships.map((m) => m.org_id), ...talentLinks.map((l) => l.org_id)])];
  const staffOrgs = [...new Set(memberships.map((m) => m.org_id))];
  const [profiles, portalNames, kinds] = orgIds.length
    ? await Promise.all([
        api.select<OrgProfile>("org_profiles", { filters: [["org_id", "in", orgIds]] }),
        // Portalname für Kopf und Seitentitel (Befund 6); fehlt die Sicht (ältere Datenbank), gilt die Wortmarke
        api.select<{ org_id: string; portal_name: string | null }>("org_portal_names", { filters: [["org_id", "in", orgIds]] }).catch(() => []),
        // Art des Kontos (0178) liest nur das Team; ältere Datenbank ohne Spalte: alles Agenturen
        staffOrgs.length
          ? api.select<{ id: string; kind: "agency" | "creator" }>("organizations", { columns: "id, kind", filters: [["id", "in", staffOrgs]] }).catch(() => [])
          : Promise.resolve([] as { id: string; kind: "agency" | "creator" }[]),
      ])
    : [[], [], []];
  const names = new Map(portalNames.map((n) => [n.org_id, n.portal_name]));
  const kindOf = new Map(kinds.map((k) => [k.id, k.kind]));
  const talents = talentLinks.length
    ? await api.select<{ id: string; display_name: string | null; first_name: string; last_name: string | null }>("talents", {
        columns: "id, display_name, first_name, last_name",
        filters: [["id", "in", talentLinks.map((l) => l.talent_id)]],
      })
    : [];
  return {
    session,
    memberships,
    talentLinks,
    orgs: Object.fromEntries(profiles.map((p) => [p.org_id, { ...p, portal_name: names.get(p.org_id) ?? null, kind: kindOf.get(p.org_id) ?? "agency" }])),
    isPlatformAdmin: admins.length > 0,
    platformMfaRequired: admins.length > 0 ? admins[0]!.mfa_required !== false : undefined,
    initials: prefs[0]?.initials ?? null,
    language: prefs[0]?.language ?? null,
    dateFormat: prefs[0]?.date_format ?? null,
    startArea: prefs[0]?.start_area === "agentur" || prefs[0]?.start_area === "betrieb" ? prefs[0].start_area : null,
    talentNames: Object.fromEntries(talents.map((t) => [t.id, t.display_name ?? `${t.first_name} ${t.last_name ?? ""}`.trim()])),
  };
}

const SessionCtx = createContext<{ session: Session | null; ready: boolean }>({ session: null, ready: false });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ session: Session | null; ready: boolean }>({ session: null, ready: false });
  const qc = useQueryClient();
  useEffect(() => {
    let alive = true;
    // Zuletzt bekannte Person: Nur ein Personenwechsel (Abmelden, anderes Konto) leert den Abfrage-Speicher. Ein
    // Token-Refresh (TOKEN_REFRESHED, etwa stündlich und aus anderen Tabs) für dieselbe Person lädt nur neu, damit offene
    // Formulare erhalten bleiben (G-WEB-2).
    let known: string | null = api.session()?.userId ?? null;
    void api.whenReady().then(() => {
      if (!alive) return;
      const s = api.session();
      known = s?.userId ?? null;
      setState({ session: s, ready: true });
    });
    const off = api.onSessionChange((s) => {
      const next = s?.userId ?? null;
      if (next !== known) {
        known = next;
        qc.clear();
        // Vorgemerkte Zustimmung aus der Registrierung gilt nur für die registrierende Person (G-WEB-4)
        forgetPendingConsentUnlessFor(s);
      } else {
        void qc.invalidateQueries();
      }
      setState({ session: s, ready: true });
    });
    return () => {
      alive = false;
      off();
    };
  }, [qc]);
  return <SessionCtx.Provider value={state}>{children}</SessionCtx.Provider>;
}

export function useSession() {
  return useContext(SessionCtx);
}

export function useAccount() {
  const { session, ready } = useSession();
  const q = useQuery({ queryKey: ["account", session?.userId], enabled: !!session, queryFn: () => loadAccount(session!), staleTime: 60_000 });
  return { session, ready, account: q.data, loading: !ready || (!!session && q.isPending), error: q.error as Error | null };
}

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
// Gewählte Agentur (Team und Creator): Wahl dieses Fensters (Speicher des Moduls, sessionStorage für Neuladen) plus
// Zähler, damit die Bereichsrahmen beim Wechsel sofort neu zeichnen. Das neu geladene Konto ist inhaltlich gleich; ohne
// Zähler blieb die Seite bei der alten Agentur (Befund 4). localStorage ist allen Tabs gemeinsam und dient nur als
// Startwert für ein neues Fenster: Die Wahl in Tab B darf Tab A nicht still umstellen (G-WEB-3).
const memory = new Map<string, string>();
function windowStored(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
const choiceListeners = new Set<() => void>();
let choiceVersion = 0;
function store(key: string, value: string) {
  memory.set(key, value);
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ohne Speicher des Fensters: Wahl gilt bis zum Neuladen */
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ohne Speicher: kein Startwert für neue Fenster */
  }
  choiceVersion += 1;
  for (const l of choiceListeners) l();
}
/**
 * Wahl nur für dieses Fenster festhalten (Speicher des Moduls und sessionStorage für Neuladen), ohne Startwert für andere
 * Fenster und ohne Neuzeichnen (GB-W1/GB-W2): gilt für die tatsächlich gezeigte Agentur, auch wenn nur die Vorgabe greift.
 */
function remember(key: string, value: string) {
  if (memory.get(key) === value && windowStored(key) === value) return;
  memory.set(key, value);
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ohne Speicher des Fensters: Wahl gilt bis zum Neuladen */
  }
}
function storedChoice(key: string): string | null {
  const own = memory.get(key) ?? windowStored(key);
  if (own) return own;
  // Neues Fenster ohne eigene Wahl: zuletzt gemerkte Agentur als Startwert, danach bleibt dieses Fenster dabei (auch nach
  // dem Neuladen, GB-W2)
  const initial = stored(key);
  if (initial) remember(key, initial);
  return initial;
}
/** Stand der Agenturwahl; Rahmen, die ihn lesen, zeichnen beim Wechsel neu. */
export function useWorkspaceChoice(): number {
  return useSyncExternalStore(
    (l) => {
      choiceListeners.add(l);
      return () => choiceListeners.delete(l);
    },
    () => choiceVersion,
    () => choiceVersion,
  );
}

/** Arbeitsbereich einer Agentur für Admins und Manager. */
export interface StaffWorkspace {
  membership: Membership;
  org: OrgProfile;
  isChef: boolean;
  can: (p: Perm) => boolean;
  name: string;
  /** Creator-Konto (0178): eine Person, ihr eigenes Profil, Rechnungen direkt an Marken */
  isCreator?: boolean;
}
export function staffWorkspace(account: Account, preferredOrg?: string | null): StaffWorkspace | null {
  // Ohne ausdrückliche Agentur: die Wahl dieses Fensters; was dann gezeigt wird, bleibt die Wahl dieses Fensters (GB-W1)
  const ownWindow = preferredOrg === undefined;
  const preferred = ownWindow ? storedChoice("tk-org") : preferredOrg;
  // Nur Agenturen mit lesbarem Profil: eine gesperrte oder gekündigte Agentur (nur für den Admin lesbar) darf den
  // Zugang zu einer zweiten, aktiven Agentur nicht versperren (RECHTE-4)
  const usable = account.memberships.filter((x) => !!account.orgs[x.org_id]);
  const m = usable.find((x) => x.org_id === preferred) ?? usable[0];
  if (!m) return null;
  if (ownWindow) remember("tk-org", m.org_id);
  const org = account.orgs[m.org_id]!;
  const isChef = m.role === "chef_manager";
  const can = (p: Perm): boolean => {
    if (isChef) return true;
    switch (p) {
      case "approve_invoices": return m.can_approve_invoices;
      case "send_invoices": return m.can_send_invoices;
      case "dun": return m.can_dun;
      case "confirm_payments": return m.can_confirm_payments;
      case "trigger_payouts": return m.can_trigger_payouts;
      case "sign_contracts": return m.can_sign_contracts;
      case "edit_templates": return !!m.can_edit_templates;
      case "edit_contract_text": return !!m.can_edit_contract_text;
      // Kennzahlen setzen voraus, dass die Person Beträge sehen darf
      case "view_financials": return !!m.can_view_financials && m.can_view_amounts !== false;
      case "view_amounts": return m.can_view_amounts !== false;
      default: return false;
    }
  };
  return { membership: m, org, isChef, can, name: m.display_name ?? account.session.email, isCreator: org.kind === "creator" };
}

/** Mitgliedschaften in Agenturen, die gerade nur für den Admin lesbar sind (gesperrt oder gekündigt, 0042). */
export function lockedStaffOrgs(account: Account): string[] {
  return account.memberships.filter((m) => !account.orgs[m.org_id]).map((m) => m.org_id);
}

/**
 * Profil bei einer früheren Agentur, die den Creator archiviert hat (0045, my_archived_workspaces): nur noch Belege
 * (Rechnungen, Verträge) und das Melden offener Auszahlungen.
 */
export interface ArchivedWorkspace {
  talent_id: string;
  org_id: string;
  name: string;
  legal_name: string | null;
  locale: string;
  currency: string;
  timezone: string;
  country: string | null;
  default_document_language: string | null;
  talent_name: string | null;
}

/** Arbeitsbereich eines Talents: eine Agentur zur Zeit, Wechsel leert alle geladenen Daten. */
export interface TalentWorkspace {
  link: TalentLink;
  org: OrgProfile;
  talentName: string;
  others: { org_id: string; name: string; archived?: boolean }[];
  /** Archiviertes Profil: nur Belege lesen und Auszahlungen melden */
  archived?: boolean;
}
export function talentWorkspace(account: Account, preferredOrg?: string | null, archived: readonly ArchivedWorkspace[] = []): TalentWorkspace | null {
  // Ohne ausdrückliche Agentur: die Wahl dieses Fensters; was dann gezeigt wird, bleibt die Wahl dieses Fensters (GB-W1)
  const ownWindow = preferredOrg === undefined;
  if (ownWindow) {
    const ws = talentWorkspace(account, storedChoice("tk-talent-org"), archived);
    if (ws) remember("tk-talent-org", ws.org.org_id);
    return ws;
  }
  // Profile ohne lesbare Agentur sind archiviert (org_profiles zeigt nur aktive Profile); sie zählen nur mit Angaben aus 0045
  const active = account.talentLinks.filter((l) => account.orgs[l.org_id] && !archived.some((a) => a.talent_id === l.talent_id));
  const others = [
    ...active.map((l) => ({ org_id: l.org_id, name: account.orgs[l.org_id]!.name })),
    ...archived.filter((a) => !active.some((l) => l.org_id === a.org_id)).map((a) => ({ org_id: a.org_id, name: a.name, archived: true })),
  ];
  const link = active.find((l) => l.org_id === preferredOrg);
  const old = link ? undefined : archived.find((a) => a.org_id === preferredOrg && !active.some((l) => l.org_id === a.org_id));
  if (old) {
    return {
      link: { talent_id: old.talent_id, org_id: old.org_id },
      org: {
        org_id: old.org_id, name: old.name, legal_name: old.legal_name, logo_path: null, brand_color: null, locale: old.locale, currency: old.currency,
        timezone: old.timezone, country: old.country, default_document_language: old.default_document_language,
      },
      talentName: old.talent_name ?? account.session.email,
      others,
      archived: true,
    };
  }
  const chosen = link ?? active[0];
  if (!chosen) {
    const first = archived[0];
    return first ? talentWorkspace(account, first.org_id, archived) : null;
  }
  return {
    link: chosen,
    org: account.orgs[chosen.org_id]!,
    talentName: account.talentNames[chosen.talent_id] ?? account.session.email,
    others,
  };
}

/** Frühere Agenturen des angemeldeten Creators (nur, wenn es Profile ohne lesbare Agentur gibt). */
export function useArchivedWorkspaces(account: Account | undefined) {
  const maybe = !!account && account.talentLinks.some((l) => !account.orgs[l.org_id]);
  return useQuery({
    queryKey: ["account", account?.session.userId, "archived"],
    enabled: maybe,
    staleTime: 60_000,
    queryFn: () => api.read<ArchivedWorkspace[]>("my_archived_workspaces", {}).catch(() => [] as ArchivedWorkspace[]),
  });
}

/** Der Bereichsrahmen (AgencyArea/CreatorArea) legt den Arbeitsbereich fest; Seiten lesen ihn von hier. */
export const StaffWorkspaceContext = createContext<StaffWorkspace | null>(null);
export const TalentWorkspaceContext = createContext<TalentWorkspace | null>(null);

export function useStaffWorkspace(): StaffWorkspace {
  const fromArea = useContext(StaffWorkspaceContext);
  const { account } = useAccount();
  if (fromArea) return fromArea;
  const ws = account ? staffWorkspace(account) : null;
  if (!ws) throw new Error(t("Kein Agentur-Arbeitsbereich", "No agency workspace"));
  return ws;
}
export function useTalentWorkspace(): TalentWorkspace {
  const fromArea = useContext(TalentWorkspaceContext);
  const { account } = useAccount();
  useWorkspaceChoice();
  if (fromArea) return fromArea;
  const ws = account ? talentWorkspace(account) : null;
  if (!ws) throw new Error(t("Kein Creator-Arbeitsbereich", "No creator workspace"));
  return ws;
}

export function useSwitchTalentOrg() {
  const qc = useQueryClient();
  return (orgId: string) => {
    store("tk-talent-org", orgId);
    // Regel aus dem Rechte-Modell: Agenturwechsel leert Abfragen, Dateien, Meldungen und Entwürfe.
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "account" });
    void qc.invalidateQueries({ queryKey: ["account"] });
  };
}
export function useSwitchStaffOrg() {
  const qc = useQueryClient();
  return (orgId: string) => {
    store("tk-org", orgId);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "account" });
    void qc.invalidateQueries({ queryKey: ["account"] });
  };
}
